<template>
  <div>
    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
      </template>
      <v-tabs v-model="tab" class="stock-color" left dark icons-and-text>
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-1">
          <v-toolbar-title>{{ $t("STOCK.TRANSPORT_ORDERS.TITLE") }}</v-toolbar-title>
        </v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab">
          <v-btn
            v-if="hasPermissions('stock-transport-orders-create')"
            class="mr-4 success"
            :disabled="isProcessing"
            @click="$router.push({ name: 'stock-transport-orders-create' })"
          >
            {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
          </v-btn>
        </div>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card flat>
            <v-card-text class="pa-0">
              <Table
                v-if="userStatePrepared"
                ref="stock-transport-orders-table"
                height="500"
                name="stock-transport-orders-table"
                :headers="headers"
                :blink-row-item-key="blinkRowItemKey"
                :server-data-options="{ method: tableItemsMethod }"
                :items-per-page="25"
                :loading="isProcessing"
                :filters="tableFilters"
                dense
                fixed-header
                filterable
              >
                <template
                  v-for="(header, index) in headers"
                  v-slot:[`item.${header.value}`]="{ item }"
                >
                  <template v-if="header.value === 'code'">
                    <router-link
                      :key="index"
                      :to="{ name: 'stock-transport-orders-view', params: { id: item.item.id } }"
                    >
                      {{ item.item.code }}
                    </router-link>
                  </template>
                  <template v-else-if="header.value === 'status'">
                    <v-chip :key="index" small :color="statusColor(item.item.status)" dark>
                      {{ statusLabel(item.item.status) }}
                    </v-chip>
                  </template>
                  <template v-else-if="header.value === 'direction'">
                    {{ directionLabel(item.item.direction) }}
                  </template>
                  <template v-else-if="header.value === 'transport_mode'">
                    {{ transportModeLabel(item.item.transport_mode) }}
                  </template>
                  <template v-else-if="header.value === 'carrier_company.label'">
                    {{ carrierLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'organization.name'">
                    {{ organizationLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'deal.label'">
                    <router-link
                      v-if="item.item.deal && item.item.deal.id"
                      :key="index"
                      :to="{ name: 'deals-view', params: { id: item.item.deal.id } }"
                    >
                      {{ dealLabel(item.item) }}
                    </router-link>
                    <span v-else :key="index">-</span>
                  </template>
                  <template v-else-if="header.value === 'agreed_cost_net'">
                    {{ formatMoney(item.item.agreed_cost_net, item.item.currency_code) }}
                  </template>
                  <template v-else>
                    {{ item.value || "-" }}
                  </template>
                </template>
              </Table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import userStateService from "@/common/userState.service";
import { GET_STOCK_TRANSPORT_ORDERS, GET_STOCK_TRANSPORT_ORDER_STATUSES, GET_STOCK_TRANSPORT_DIRECTIONS } from "@/store/stock/stockTransportOrders.module";
import { GET_STOCK_DEAL_OPTIONS } from "@/store/stock/stockReceipts.module";

export default {
  name: "StockListTransportOrders",
  components: { Table },
  data() {
    return {
      tableName: "stock-transport-orders-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      tableItemsMethod: GET_STOCK_TRANSPORT_ORDERS,
      tableFilters: [],
      headers: [
        { text: this.$t("STOCK.TRANSPORT_ORDERS.DOCUMENT_CODE"), value: "code", sortable: true, align: "center", width: "10%" },
        { text: this.$t("GENERAL.LABELS.STATUS"), value: "status", sortable: true, align: "center", width: "8%" },
        { text: this.$t("STOCK.TRANSPORT_ORDERS.DIRECTION"), value: "direction", sortable: true, align: "center", width: "8%" },
        { text: this.$t("STOCK.TRANSPORT_ORDERS.TRANSPORT_MODE"), value: "transport_mode", sortable: true, align: "center", width: "9%" },
        { text: this.$t("STOCK.TRANSPORT_ORDERS.CARRIER"), value: "carrier_company.label", sortable: false, align: "center", width: "12%" },
        { text: this.$t("STOCK.WAREHOUSES.ORGANIZATION"), value: "organization.name", sortable: false, align: "center", width: "10%" },
        { text: this.$t("STOCK.TRANSPORT_ORDERS.DEAL"), value: "deal.label", sortable: false, align: "center", width: "12%" },
        { text: this.$t("STOCK.TRANSPORT_ORDERS.SCHEDULED_LOADING"), value: "scheduled_loading_at", sortable: true, align: "center", width: "9%" },
        { text: this.$t("STOCK.TRANSPORT_ORDERS.AGREED_COST"), value: "agreed_cost_net", sortable: false, align: "center", width: "9%" },
      ],
    };
  },
  computed: {
    stockContextVersion() {
      return this.$store.getters.stockContextVersion;
    },
  },
  watch: {
    stockContextVersion() {
      this.loadTableFilters().finally(() => this.reloadTable());
    },
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );
    this.loadTableFilters().finally(() => {
      this.userStatePrepared = true;
    });
  },
  methods: {
    carrierLabel(row) {
      const company = row.carrier_company;
      if (!company) {
        return "-";
      }
      return company.label || company.name || "-";
    },
    organizationLabel(row) {
      const org = row.organization;
      if (!org) {
        return "-";
      }
      return org.name || org.code || "-";
    },
    dealLabel(row) {
      const deal = row.deal;
      if (!deal) {
        return "-";
      }
      if (deal.code && deal.name) {
        return `${deal.code} — ${deal.name}`;
      }
      return deal.name || deal.code || "-";
    },
    loadTableFilters() {
      const organizationIds = this.$store.getters.stockContextOrganizationIds;

      const dealParams = { limit: 100 };
      if (organizationIds.length === 1) {
        dealParams.organization_id = organizationIds[0];
      } else if (organizationIds.length > 1) {
        dealParams.organization_ids = organizationIds;
      }

      return Promise.all([
        this.$store.dispatch(GET_STOCK_TRANSPORT_ORDER_STATUSES),
        this.$store.dispatch(GET_STOCK_TRANSPORT_DIRECTIONS),
        this.$store.dispatch(GET_STOCK_DEAL_OPTIONS, dealParams).catch(() => []),
      ])
        .then(([statuses, directions, deals]) => {
          const statusItems = (statuses || []).map((status) => ({
            id: status.slug,
            label: this.lookupStatusLabel(status),
          }));

          const directionItems = (directions || []).map((direction) => ({
            id: direction.slug,
            label: this.lookupDirectionLabel(direction),
          }));

          const dealItems = (deals || []).map((deal) => ({
            id: deal.id,
            label: deal.code ? `${deal.code} — ${deal.name}` : deal.name,
          }));

          this.tableFilters = [
            {
              key: "status",
              label: this.$t("GENERAL.LABELS.STATUS"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: statusItems,
            },
            {
              key: "direction",
              label: this.$t("STOCK.TRANSPORT_ORDERS.DIRECTION"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: directionItems,
            },
            {
              key: "deal_id",
              label: this.$t("STOCK.TRANSPORT_ORDERS.DEAL"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: dealItems,
            },
          ];
        })
        .catch(() => {
          this.tableFilters = [];
        });
    },
    lookupStatusLabel(status) {
      const slug = (status.slug || "").toUpperCase();
      const key = `STOCK.STATUSES.TO.${slug}`;
      return this.$te(key) ? this.$t(key) : status.name || status.slug || "-";
    },
    lookupDirectionLabel(direction) {
      const slug = (direction.slug || "").toUpperCase();
      const key = `STOCK.TRANSPORT_ORDERS.DIRECTIONS.${slug}`;
      return this.$te(key) ? this.$t(key) : direction.name || direction.slug || "-";
    },
    reloadTable() {
      this.$nextTick(() => {
        this.$refs["stock-transport-orders-table"]?.fetchServerData();
      });
    },
    statusColor(status) {
      const map = {
        draft: "grey",
        sent: "grey",
        agreed: "primary",
        in_transit: "orange",
        delivered: "success",
        cancelled: "error",
      };
      return map[status] || "grey";
    },
    statusLabel(status) {
      const key = `STOCK.STATUSES.TO.${(status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : status;
    },
    directionLabel(direction) {
      const key = `STOCK.TRANSPORT_ORDERS.DIRECTIONS.${(direction || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : direction;
    },
    transportModeLabel(mode) {
      const key = `STOCK.TRANSPORT_ORDERS.MODES.${(mode || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : mode;
    },
    formatMoney(value, currency) {
      if (value == null || value === "") {
        return "-";
      }
      return `${Number(value || 0).toFixed(2)} ${currency || ""}`.trim();
    },
  },
};
</script>
